/**
 * Rules the sim plays by that changed after a version shipped, so a run can be replayed exactly by
 * the rules it was played on (docs/ONLINE.md, "Rules versions"): the game plays by
 * {@link SIM_RULES}; the server checks a run from an older version with what that version had.
 */
export interface SimRules {
  /**
   * A squirrel or rabbit in the mower's way poofs off the lawn in a white puff for a while: no
   * crash, no heart lost, the mower drives on (owner, 2026-10-28; rules version 6). Off: a crash.
   */
  softSmallCritters: boolean;
  /**
   * Herding: a turn may go into a sheep, which is then nudged on like one straight ahead (owner's
   * bug report, 2026-10-28; rules version 6). Off: a sheep blocks a turn.
   */
  turnIntoSheep: boolean;
  /**
   * Mower soccer: a turn may go into the ball, which the mower then kicks on as it drives off
   * (owner's bug report, 2026-10-29; rules version 9). Off: the ball blocks a turn like a wall.
   */
  turnIntoBall: boolean;
  /**
   * The Fairway's side reels pick up coins (and everything else) in a secret yard beyond the
   * lawn too (owner's bug report, 2026-10-29; rules version 9). Off: only on the lawn itself.
   */
  wingsBeyondLawn: boolean;
  /**
   * The Fairway's side reels pushing into tall grass slow it down, as its middle reel does (owner's
   * bug report, 2026-10-29; rules version 15). Off: only the middle reel's grass counts.
   */
  wingsInTallGrass: boolean;
}

/** The rules the game plays by now. */
export const SIM_RULES: Readonly<SimRules> = {
  softSmallCritters: true,
  turnIntoSheep: true,
  turnIntoBall: true,
  wingsBeyondLawn: true,
  wingsInTallGrass: true,
};

/** The rules of versions 9 to 14. */
export const SIM_RULES_9: Readonly<SimRules> = {
  softSmallCritters: true,
  turnIntoSheep: true,
  turnIntoBall: true,
  wingsBeyondLawn: true,
  wingsInTallGrass: false,
};

/** The rules of versions 6 to 8. */
export const SIM_RULES_6: Readonly<SimRules> = {
  softSmallCritters: true,
  turnIntoSheep: true,
  turnIntoBall: false,
  wingsBeyondLawn: false,
  wingsInTallGrass: false,
};

/** The rules before version 6. */
export const LEGACY_SIM_RULES: Readonly<SimRules> = {
  softSmallCritters: false,
  turnIntoSheep: false,
  turnIntoBall: false,
  wingsBeyondLawn: false,
  wingsInTallGrass: false,
};

/** How long a poofed squirrel or rabbit stays off the lawn, in ticks (then it comes back home). */
export const POOF_TICKS = 600;
